import {
  AnimatedImagePromptType,
  AnimatedImageStatus,
} from "../animated-image.ts";
import {
  BillingAccountCustomPricingDto,
  SubscriptionTierType,
} from "../billing.ts";
import { VideoRenderUpscaleQuality } from "../video-render.ts";

export const CREDITS_FIRST_ANIMATION = 150;

export const CREDITS_RE_ANIMATION = 50;

export const CREDITS_CREATIVE_ANIMATION_PER_SECOND = 30;
export const CREDITS_CREATIVE_IMAGE_EDIT = 20;

// 1 credit = 1 cent USD (see getOneOffTierConfig).
export const CREDITS_VIDEO_RENDER_UPSCALE_PER_SECOND: Record<
  VideoRenderUpscaleQuality,
  number
> = {
  "1080p": 5,
  "4k": 10,
};

export function calculateVideoRenderUpscaleCredits(
  durationMs: number,
  quality: VideoRenderUpscaleQuality,
) {
  const perSecond = CREDITS_VIDEO_RENDER_UPSCALE_PER_SECOND[quality];
  return Math.ceil(durationMs / 1000) * perSecond;
}

export function calculateCreativeAnimationCredits(
  durationSeconds: number,
  customPricing: BillingAccountCustomPricingDto | null = null,
) {
  const perSecond =
    customPricing?.creditsCustomAnimationPerSecond ??
    CREDITS_CREATIVE_ANIMATION_PER_SECOND;
  return Math.round(perSecond * durationSeconds);
}

export function calculateCreativeImageEditCredits(
  customPricing: BillingAccountCustomPricingDto | null = null,
) {
  const credits =
    customPricing?.creditsCustomImageEdit ?? CREDITS_CREATIVE_IMAGE_EDIT;
  return Math.round(credits);
}

type SubscriptionTier = {
  credits: number;
  price: number;
  currency: string;
};

export const subscriptionTiers: Map<SubscriptionTierType, SubscriptionTier> =
  new Map([
    ["MONTHLY_29", { credits: 3450, price: 2900, currency: "usd" }],
    ["MONTHLY_49", { credits: 6000, price: 4900, currency: "usd" }],
    ["MONTHLY_99", { credits: 13000, price: 9900, currency: "usd" }],
    ["MONTHLY_149", { credits: 21000, price: 14900, currency: "usd" }],
    ["MONTHLY_249", { credits: 36000, price: 24900, currency: "usd" }],
    ["MONTHLY_499", { credits: 72000, price: 49900, currency: "usd" }],
    ["MONTHLY_799", { credits: 120000, price: 79900, currency: "usd" }],
    ["MONTHLY_1199", { credits: 180000, price: 119900, currency: "usd" }],
  ]);

export function getSubscriptionTierConfig(tier: SubscriptionTierType) {
  const tierConfig = subscriptionTiers.get(tier);
  if (!tierConfig) {
    throw new Error(`No tier found for ${tier}`);
  }
  return tierConfig;
}

type OneOffTier = {
  credits: number;
  price: number;
  currency: string;
};

// 100 credits = 100 cents (USD)
export function getOneOffTierConfig(credits: number): OneOffTier {
  return {
    credits,
    price: credits,
    currency: "usd",
  };
}

export function getBaseCreditsForPrice(price: number) {
  return price;
}

export function getMinCredits() {
  return CREDITS_FIRST_ANIMATION;
}

export function getMinPrice() {
  return CREDITS_FIRST_ANIMATION;
}

export function getMaxPrice() {
  return 1_000_000;
}

export function calculateImages(credits: number) {
  return Math.floor(credits / CREDITS_FIRST_ANIMATION);
}

export function calculateSavePercentage(price: number, credits: number) {
  const oneOffPrice = getOneOffTierConfig(credits).price;

  if (oneOffPrice === price) {
    return 0;
  }

  return ((oneOffPrice - price) / oneOffPrice) * 100;
}

export type AnimatedImageForCredits = {
  status: AnimatedImageStatus;
  promptType: AnimatedImagePromptType | null;
};

export function calculateCreditsForImage(
  animatedImages: AnimatedImageForCredits[],
  activeSubscriptionTier: SubscriptionTierType | null,
  customPricing: BillingAccountCustomPricingDto | null = null,
) {
  // Creative Mode (CUSTOM promptType) animations have a separate billing model
  // and must not affect Quick Animation re-animation pricing.
  // Uploaded animations (null promptType) have no AI cost and are not billed.
  const billedAnimatedImages = animatedImages.filter(
    (animatedImage) =>
      animatedImage.status !== "FAILED" &&
      animatedImage.promptType !== "CUSTOM" &&
      animatedImage.promptType !== null,
  );

  if (billedAnimatedImages.length === 0) {
    return customPricing?.creditsFirstAnimation ?? CREDITS_FIRST_ANIMATION;
  }

  const freeReAnimations =
    customPricing?.numFreeReAnimations ?? (activeSubscriptionTier ? 10 : 1);

  if (billedAnimatedImages.length <= freeReAnimations) {
    return 0;
  }

  return customPricing?.creditsReAnimation ?? CREDITS_RE_ANIMATION;
}
